import {
    createContext,
    MutableRefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { GetServerSidePropsContext } from 'next';
import { CONTENT_TYPE_VALUE, TIME_SET_CONTENT } from 'src/constants';
import { IResponse, IPageData, IContentDetail } from 'src/interface';
import { ArrayUtils, ObjectUtils } from 'src/utils';
import { useViewTime } from 'src/hooks';
import { trackingPageDetail } from 'src/utils/tracking';
import { setIdsRead } from 'src/utils/content';
import PageService from 'src/services/PageService';
import { ContentDispatchActions } from 'src/redux/actions';
import { strUserIdSelector } from 'src/selectors/auth';
import { Element, IconButton, LazyLoad, RouterLink } from 'src/components';
import { GATracking } from 'src/modules/shared/components/GA';
import {
    ContentMain,
    ContentSocial,
    ContentSource,
    ContentsHistory,
    ContentsRelated,
    EventSoccer,
} from 'src/modules/content/views';

import styles from './style.module.scss';

export const ContentDetailContext = createContext<any>(null);

export function ContentDetail({
    resp,
    isBot = false,
}: {
    resp: IResponse<IPageData<IData>>;
    isBot: boolean;
}) {
    const dispatch = useDispatch();
    const data = resp?.data || ({} as any);
    const { related = [], content = {} as any } = data;
    const strUserId = useSelector(strUserIdSelector);
    const router = useRouter();
    const intervalRef: MutableRefObject<any> = useRef(1);

    const iframeRef = useRef<any>(null);
    const [height, setHeight] = useState<number>(0);

    const renderedKeys = useRef(new Map());

    const register = (key: string, id: number) => {
        if (!renderedKeys.current.has(key)) {
            renderedKeys.current.set(key, id);
            return true;
        }

        return renderedKeys.current.get(key) === id;
    };

    const release = (key: string, id: number) => {
        if (renderedKeys.current.get(key) === id) {
            renderedKeys.current.delete(key);
        }
    };

    useEffect(() => {
        if (content.contentId && strUserId) {
            trackingPageDetail(content.contentId, strUserId);
        }
    }, [content.contentId, strUserId]);

    useEffect(() => {
        if (intervalRef && intervalRef.current) {
            intervalRef.current = setInterval(() => {
                setIdsRead(content.contentId);
                clearInterval(intervalRef.current);
                dispatch(ContentDispatchActions.setReadId(content.contentId));
            }, TIME_SET_CONTENT);
        }
        return () => {
            clearInterval(intervalRef.current);
        };
    }, [content]);

    useEffect(() => {
        if (!iframeRef.current) {
            return;
        }
        const setHeightIframe = (e: any) => {
            const eventName = e.data[0];
            var data = e.data[1];

            switch (eventName) {
                case 'setHeight':
                    setHeight(data);
                    break;
            }
        };
        const layout = document.getElementById('layout');
        layout?.classList.add(styles['embed-layout']);
        window.addEventListener('message', setHeightIframe, false);
        return () => {
            window.removeEventListener('message', setHeightIframe, false);
        };
    }, [iframeRef]);

    useEffect(() => {
        if (ObjectUtils.isEmpty(content)) {
            router.replace('/');
        }
    }, []);

    const { ref } = useViewTime({
        tracking: 'page',
        contentId: content.contentId,
        threshold: 0,
        onFinish: () => {},
    });

    if (ObjectUtils.isEmpty(content)) {
        return null;
    }

    return (
        <ContentDetailContext.Provider value={{ register, release }}>
            <Element domRef={ref}>
                <GATracking content={content} />
                <>
                    {content.embedUrl ? (
                        <iframe
                            ref={iframeRef}
                            id="ifrademe"
                            style={{
                                display: 'block',
                                position: 'relative',
                                width: 'calc(100% + 32px)',
                                marginLeft: '-16px',
                                marginTop: '-16px',
                                height:
                                    height === 0
                                        ? 'calc(100vh - 90px)'
                                        : height,
                            }}
                            src={content.embedUrl}
                            title=""
                            frameBorder="0"
                        />
                    ) : (
                        <>
                            <Element
                                className={cn(styles['content-wrapper'], {
                                    [styles['is-live']]:
                                        content?.contentTypes?.includes(
                                            CONTENT_TYPE_VALUE.LIVE,
                                        ),
                                })}
                            >
                                <ContentMain {...content} lazyLoad={!isBot} />
                                <ContentsHistory content={content} />
                                <Element className="mb-[24px] empty:hidden">
                                    <ContentSocial
                                        id={-1}
                                        contentId={content.contentId}
                                        title={content.title}
                                        url={content.url}
                                    />
                                </Element>
                                <ContentSource
                                    publisher={content.publisher}
                                    originalUrl={content.originalUrl}
                                    redirectUrl={content.redirectUrl}
                                    tags={content.topTags}
                                />

                                <EventSoccer content={content} />
                            </Element>
                            {ArrayUtils.isEmpty(related) ? (
                                <LazyLoad>
                                    <ContentsRelated
                                        contentId={content.contentId as any}
                                    />
                                </LazyLoad>
                            ) : (
                                <ContentsRelated
                                    contentId={content.contentId as any}
                                    related={related}
                                    isBot={true}
                                />
                            )}

                            <RouterLink to="/" className="flex justify-center">
                                <IconButton
                                    className={cn(
                                        'bg-[var(--primary)]',
                                        'text-white text-[16px] uppercase',
                                        'h-[48px]',
                                        'px-[20px] mt-[32px]',
                                        'rounded-full',
                                        'flex items-center',
                                    )}
                                    classNameIcon={cn('text-[24px] mr-[12px]')}
                                    title="Về trang chủ"
                                    text="Về trang chủ"
                                    icon="new-home-regular"
                                ></IconButton>
                            </RouterLink>
                        </>
                    )}
                </>
            </Element>
        </ContentDetailContext.Provider>
    );
}

interface IData extends IContentDetail {
    description: string;
    originalUrl?: string;
    bodys: any[];
    speechs: any[];
}

export async function getServerSideProps(
    ctx: GetServerSidePropsContext,
): Promise<any> {
    return PageService.get(ctx, 'content-detail', { id: ctx?.params?.id });
}

export default ContentDetail;
